/*
 * ==============================================================================
 *
 * Copyright (c) 2023. Koninklijke Philips Electronics N.V.
 *
 * All rights are reserved. Reproduction in whole or in part is prohibited without the prior written consent of the copyright holder.
 *
 * ==============================================================================
 */

import React, { useContext, useState, useRef } from "react";
import { useToasts } from "react-toast-notifications";
import { LayoutContext } from "../../components/layout";
import { Dashboard2Context } from "../dashboard2";
import fetchHelper from "../../services/fetchHelper";
import { finalReportViewService } from "../FinalReportViewService";
import ModalAnticoagulant from "./ModalAnticoagulant";
import ModalRemoveInterrogation from "./ModalRemoveInterrogation";
import { featureCheck } from "../../utils/feature";
import dayjs from "dayjs";
import "./SaveSign.css";

import { PAMV1RestUrl } from "../../constants/PatientManagementConstants";
import { BillStatus } from "../../constants/InterrogationConstans";

export function SaveSign({
  onFollowUpChange,
  isLoading,
  setIsLoading,
  nurseFollowUp,
  nurseFollowUpLoading,
  setNurseFollowUp,
  loadAssignerInfo,
}) {
  const action = useRef("");
  const { addToast } = useToasts();
  const { setShowDownload, newInbox, fetchI } = useContext(Dashboard2Context);
  const [dataAnticoagulant, setDataAnticoagulant] = useState(null);
  const [showModalRemove, setShowModalRemove] = useState(false);
  const { headerDetails: userInfo, user } = useContext(LayoutContext);

  const start = parseInt(localStorage.getItem("inboxCurrentIndex") || 0);

  const goToNextIR = async (currentIrID) => {
    if (newInbox) {
      const currentIrIndex = newInbox.findIndex((i) => i.id === currentIrID);
      action.current = "";
      if (currentIrIndex !== -1) {
        if (currentIrIndex + 1 < newInbox.length)
          window.location.replace(`/dashboard3/${newInbox[currentIrIndex + 1].id}`);
        else {
          window.location.replace(`/reports3`);
        }
      } else {
        window.location.replace(`/reports3`);
      }
    }
  };

  const updateInterrogationStatus = async () => {
    try {
      if (isLoading) {
        return;
      }

      if (
        !showModalRemove &&
        (userInfo.thirtyDayBillStatus == BillStatus.thirtyDay ||
          userInfo.nintyDayBillStatus == BillStatus.ninetyDay)
      ) {
        setShowModalRemove(true);
        return;
      }

      setIsLoading(true);
      setShowModalRemove(false);
      const url = `/patientmanagement/rest/interrogations/${userInfo.id}`;
      const response = await fetch(url, {
        method: "PUT",
        headers: fetchHelper.getSecurityHeader(),
        body: JSON.stringify({
          interrogationStatus: "TERMINATED",
          id: userInfo.id,
        }),
      });

      const responseBody = await response.json();
      if (response.ok) {
        await fetchI(userInfo.id);
      } else {
        handleErrors(responseBody);
      }
    } catch (err) {
      console.error("ERR", err);
    } finally {
      setIsLoading(false);
      loadAssignerInfo(userInfo.id);
    }
  };

  const onSubmit = async (e) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }

    try {
      if (isLoading) return;
      setIsLoading(true);
      const url = `/patientmanagement/rest/interrogations/${userInfo.id}/eSign?async=yes`;
      const response = await fetch(url, {
        method: "PUT",
        headers: fetchHelper.getSecurityHeader(),
        body: JSON.stringify({
          Statements: [
            { id: 1, isSelected: true },
            { id: 2, isSelected: true },
            { id: 3, isSelected: true },
            { id: 4, isSelected: true },
            { id: 5, isSelected: true },
          ],
          id: userInfo.id,
        }),
      });
      setShowDownload(true);
      await fetchI(userInfo.id);
      localStorage.setItem("onEsignSubmitted", `${true}`);
      setIsLoading(false);
      const { response: pdfUUID } = await response.json();
      finalReportViewService.clickEsignSubmit(pdfUUID);
    } catch (err) {
      setIsLoading(false);
    }
  };

  /**
   * Check the Anticoagulant status to show or hide the modal
   */
  const checkACStatus = async () => {
    try {
      const data = await fetchHelper.get({
        url: `${PAMV1RestUrl}/patient/${userInfo.patient.id}/anticoagulant-configuration`,
      });

      if (data?.lastDateUpdated) {
        const formatLastDateUpdated = dayjs(data.lastDateUpdated);
        const formatInterrogationDate = dayjs(userInfo.interrogationDate);
        if (formatInterrogationDate < formatLastDateUpdated) {
          data.confirmed = true;
        }
      }

      setDataAnticoagulant(data);
      if (data.confirmed || Object.keys(data).length === 0) {
        handleSubmit();
      }
    } catch (err) {
      handleSubmit();
    }
  };

  const handleSubmit = async () => {
    if (dataAnticoagulant && Object.prototype.hasOwnProperty.call(dataAnticoagulant, "confirmed")) {
      dataAnticoagulant.confirmed = true;
      setDataAnticoagulant(dataAnticoagulant);
    }
    await onSubmit();
    if (action.current === "SIGN_NEXT") {
      await goToNextIR(userInfo.id);
    }
  };

  const handleErrors = ({ errors }) => {
    errors.forEach((err) => {
      addToast(err.msg || err.errorMessage, {
        appearance: "error",
        autoDismiss: false,
      });
    });
  };

  return (
    <>
      <div className="save-sign-container">
        <div className="save-sign-content">
          <label className="cursor-pointer">
            <input
              className="tgl tgl-light"
              id="cb1"
              type="checkbox"
              checked={nurseFollowUp}
              onChange={() => {
                setNurseFollowUp(!nurseFollowUp);
              }}
              onClick={onFollowUpChange}
            />
            <label className="tgl-btn" htmlFor="cb1" />
            <span className="lbl-txt nurse-toggle-wrapper">
              Nurse Follow Up
              {nurseFollowUpLoading && (
                <div className="saving-teal-ico icon-64" style={{ marginLeft: 5, width: 20, height: 20 }} />
              )}
            </span>
          </label>
          <div
            className="btn-save-sign"
            style={{ display: "flex", flexWrap: "wrap", justifyContent: "flex-end" }}
          >
            {userInfo?.readingPhysician?.id !== user.id ||
            ["TERMINATED", "FINAL"].includes(userInfo.interrogationStatus) ? null : (
              <>
                <button
                  id="signId"
                  className="btn btn-teal-l"
                  onClick={async (e) => {
                    action.current = "SIGN";
                    if (!featureCheck("anticoagulantStatement.enable")) {
                      await onSubmit(e);
                    } else {
                      await checkACStatus();
                    }
                  }}
                  disabled={isLoading}
                  type="button"
                >
                  {isLoading && action.current === "SIGN" ? (
                    <div
                      id="results-loading-icon"
                      className="saving-white-ico icon-64"
                      style={{ width: 15, height: 15, marginRight: 3 }}
                    />
                  ) : null}
                  <span>SIGN</span>
                </button>
                {newInbox && newInbox.length > 0 ? (
                  <>
                    <button
                      id="signNextId"
                      className="btn btn-success"
                      onClick={async (e) => {
                        action.current = "SIGN_NEXT";
                        if (!featureCheck("anticoagulantStatement.enable")) {
                          await onSubmit(e);
                          await goToNextIR(userInfo.id);
                        } else {
                          await checkACStatus();
                        }
                      }}
                      disabled={isLoading}
                      type="button"
                    >
                      {isLoading && action.current === "SIGN_NEXT" ? (
                        <div
                          id="results-loading-icon-2"
                          className="saving-white-ico icon-64"
                          style={{ width: 15, height: 15, marginRight: 3 }}
                        />
                      ) : null}
                      <span>SIGN & NEXT</span>
                    </button>
                    <button
                      id="nextId"
                      className="btn btn-success"
                      onClick={async () => {
                        localStorage.setItem("inboxCurrentIndex", `${start + 1}`);
                        await goToNextIR(userInfo.id);
                      }}
                      disabled={isLoading}
                      type="button"
                    >
                      <span>NEXT</span>
                    </button>
                  </>
                ) : null}
              </>
            )}
            <button
              id="removeId"
              className="btn btn-urgent"
              onClick={updateInterrogationStatus}
              disabled={userInfo.interrogationStatus === "TERMINATED" || isLoading}
              type="button"
            >
              {isLoading && (
                <div
                  id="results-loading-icon-2"
                  className="saving-white-ico icon-64"
                  style={{ width: 15, height: 15, marginRight: 3 }}
                />
              )}
              <span>
                {["TERMINATED", "FINAL"].includes(userInfo.interrogationStatus) ? "REMOVED" : "REMOVE"}
              </span>
            </button>
          </div>
        </div>
      </div>

      {featureCheck("anticoagulantStatement.enable") &&
        dataAnticoagulant &&
        Object.keys(dataAnticoagulant).length > 0 &&
        !dataAnticoagulant?.confirmed && (
          <ModalAnticoagulant
            userId={user.id}
            patientId={userInfo.patient.id}
            statementIds={dataAnticoagulant.statements}
            anticoagulantStatusId={dataAnticoagulant.anticoagulantStatusId}
            handleSubmit={handleSubmit}
          />
        )}

      {showModalRemove && (
        <ModalRemoveInterrogation
          isLoading={isLoading}
          setShowModalRemove={setShowModalRemove}
          handleSubmit={updateInterrogationStatus}
        />
      )}
    </>
  );
}
